import { useEffect } from 'react';
import { api, DeploymentGroup, DeploymentGroupMember } from '../api';
import { useResource } from '../hooks';
import { applicationTypeLabel, humanize } from '../labels';
import { ErrorNotice, LoadingRows, StatusBadge } from './ui';
import { groupHistoryLabel, groupNowValue, memberNowSentence } from './deploymentGroupState';

interface DeploymentGroupViewProps {
  groupId: string;
  onOpenDeployment: (deploymentId: string) => void;
  /** Retry one failed component (opens the deployment dialog for it). */
  onRetry: (applicationId: string) => void;
  onBack: () => void;
}

/**
 * DeploymentGroupView follows one coordinated deployment: each component
 * in the order it runs, its own status, and what to do about a failed
 * one. A failure never undoes the components that succeeded.
 */
export function DeploymentGroupView({ groupId, onOpenDeployment, onRetry, onBack }: DeploymentGroupViewProps) {
  const res = useResource<{ group: DeploymentGroup; members: DeploymentGroupMember[] }>(() => api.getDeploymentGroup(groupId), [groupId]);
  const running = res.data ? res.data.group.status === 'pending' || res.data.group.status === 'running' : false;
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => res.reload(), 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const group = res.data?.group;
  const members = res.data?.members ?? [];
  const succeeded = members.filter((m) => m.deployment.status === 'completed' || m.deployment.status === 'completed_with_failures').length;
  const failed = members.filter((m) => m.deployment.status === 'failed' || m.deployment.status === 'cancelled' || m.deployment.status === 'rolled_back').length;

  return (
    <section className="card card--wide" aria-labelledby="group-title" data-testid="deployment-group-view">
      <div className="readiness__head">
        <div>
          <h2 id="group-title" className="heading-with-badge">
            {group ? (group.scope === 'all' ? 'Deploy all' : 'Deploy selected') : 'Coordinated deployment'}
            {group ? <span data-testid="group-status"><StatusBadge kind="deploymentGroup" value={group.status} label={groupHistoryLabel(group)} /></span> : null}
            {group && groupNowValue(group) ? <span data-testid="group-now"><StatusBadge kind="groupCurrent" value={groupNowValue(group)!} /></span> : null}
          </h2>
        </div>
        <button type="button" className="button button--secondary button--small" onClick={onBack}>
          Back to components
        </button>
      </div>
      <ErrorNotice error={res.error} />
      {res.loading && !res.data ? <LoadingRows label="Loading coordinated deployment" rows={3} /> : null}
      {group ? (
        <p className="group-progress" aria-live="polite">
          {succeeded}/{members.length} live
          {failed > 0 ? ` · ${failed} stopped` : ''}
          {group.failure_reason ? <span className="muted"> — {group.failure_reason}</span> : null}
        </p>
      ) : null}
      {members.length > 0 ? (
        <ol className="group-members" data-testid="group-members">
          {members.map((m) => {
            const d = m.deployment;
            const now = group?.current?.members.find((c) => c.application_id === d.application_id);
            const sha = m.application_type === 'frontend' && d.frontend_commit_sha ? d.frontend_commit_sha : d.source_commit_sha;
            // Retrying a component that a later deployment already shipped
            // would redeploy an older commit over it.
            const retryable = (d.status === 'failed' || d.status === 'cancelled') && now?.live_now !== 'newer_release';
            return (
              <li key={d.id} className="group-member">
                <span className="group-member__seq" aria-hidden="true">{m.sequence}</span>
                <div className="group-member__main">
                  <div className="group-member__title">
                    <strong>{m.application_name}</strong>
                    <span className="muted">{applicationTypeLabel[m.application_type] ?? humanize(m.application_type)}</span>
                    <code>{sha.slice(0, 7)}</code>
                    <span data-testid={`member-${d.application_id}`}>
                      <StatusBadge kind="deployment" value={d.status} />
                    </span>
                  </div>
                  {d.failure_reason && d.status !== 'completed' ? <div className="table__sub">{d.failure_reason}</div> : null}
                  {now && (now.live_now !== 'this_release' || d.status !== 'completed') ? (
                    <div className="table__sub" data-testid={`member-now-${d.application_id}`}>
                      {memberNowSentence(now.live_now)}
                    </div>
                  ) : null}
                </div>
                <div className="group-member__actions">
                  <button type="button" className="button button--secondary button--small" onClick={() => onOpenDeployment(d.id)}>
                    View
                  </button>
                  {retryable && d.application_id ? (
                    <button type="button" className="button button--secondary button--small" onClick={() => onRetry(d.application_id!)} data-testid="member-retry">
                      Retry {m.application_name}
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      ) : null}
    </section>
  );
}
